import React, { lazy } from 'react';

import ProtectedRoute from '../ProtectedRoute';
import { AuthContext } from '../contexts/AuthContext';
import Forbidden from '../pages/Forbidden';

// Guarded pages are lazily loaded so each ships as its own bundle chunk.
// Forbidden stays eager since it is the lightweight fallback shown when access
// is denied.
const AdminsPage = lazy(() => import('../pages/Admins'));
const Claims = lazy(() => import('../pages/Claims/Claims'));
const ClaimsTool = lazy(() => import('../pages/ClaimsTool/Overview/Screen'));
const ClaimCategorization = lazy(
  () => import('../pages/ClaimsTool/Categorization/Screen'),
);
const ClaimEligibilityCheck = lazy(
  () => import('../pages/ClaimsTool/EligibilityCheck/Screen'),
);
const ClaimMakeDecision = lazy(
  () => import('../pages/ClaimsTool/MakeDecision/Screen'),
);
const ClaimViewer = lazy(
  () => import('../pages/ClaimsTool/ClaimViewer/Screen'),
);
const CrawfordCompanies = lazy(() => import('../pages/Crawford'));
const ViewActiveBP = lazy(() => import('../pages/Crawford/ListActiveBP'));
const Resources = lazy(() => import('../pages/Crawford/Resources'));
const ServiceProvider = lazy(() => import('../pages/Crawford/ServiceProvider'));
const Billing = lazy(() => import('../pages/Customers/Billing'));
const Companies = lazy(() => import('../pages/Customers/Companies'));
const AddCompany = lazy(
  () => import('../pages/Customers/Companies/AddCompany'),
);
const ViewCompany = lazy(
  () => import('../pages/Customers/Companies/ViewCompany'),
);
const Contracts = lazy(() => import('../pages/Customers/Contracts'));
const AddContract = lazy(
  () => import('../pages/Customers/Contracts/AddContract'),
);
const InvoiceTrainingPage = lazy(() => import('../pages/InvoiceTraining'));
const AIPipelinePage = lazy(() => import('../pages/AI-Pipelines'));
const Consultations = lazy(() => import('../pages/Members/Consultation'));
const Members = lazy(() => import('../pages/Members/Members'));
const StatsPage = lazy(() => import('../pages/Stats'));
const Wallets = lazy(() => import('../pages/Wallets'));
const WalletTransactions = lazy(() => import('../pages/Wallets/Transactions'));

export const ProtectedBilling = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);
  if (isCrawOnly()) {
    return <Forbidden />;
  }
  return (
    <ProtectedRoute module="accounts" action="read">
      <Billing {...props} />
    </ProtectedRoute>
  );
};

export const ProtectedAdmins = (props) => {
  const { me } = React.useContext(AuthContext);
  if (!me?.isSuperuser) {
    return <Forbidden />;
  }
  return (
    <ProtectedRoute action="write">
      <AdminsPage {...props} />
    </ProtectedRoute>
  );
};

export const ProtectedCompanies = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);

  if (isCrawOnly()) {
    return <CrawfordCompanies {...props} />;
  }
  return <Companies {...props} />;
};

export const ProtectedContract = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);
  if (isCrawOnly()) {
    return <Forbidden />;
  }
  return (
    <ProtectedRoute module="accounts" action="read">
      <Contracts {...props} />
    </ProtectedRoute>
  );
};

export const ProtectedAddContract = (props) => (
  <ProtectedRoute module="accounts" action="write">
    <AddContract {...props} />
  </ProtectedRoute>
);

export const ProtectedInvoiceTraining = (props) => (
  <ProtectedRoute module="invoice_training" action="write">
    <InvoiceTrainingPage {...props} />
  </ProtectedRoute>
);

export const ProtectedAIPipeline = (props) => (
  <ProtectedRoute module="ai_pipeline" action="write">
    <AIPipelinePage {...props} />
  </ProtectedRoute>
);

export const ProtectedClaims = (props) => (
  <ProtectedRoute module="claims" action="write">
    <Claims {...props} />
  </ProtectedRoute>
);

export const ProtectedClaimsTool = ({ children, ...props }) => {
  // TODO: remove before launch
  if (!['development', 'staging', 'preview'].includes(process.env.NODE_ENV)) {
    return <Forbidden />;
  }

  return (
    <ProtectedRoute module="claims-tool" action="write">
      {children || <ClaimsTool {...props} />}
    </ProtectedRoute>
  );
};

export const ProtectedClaimViewer = (props) => (
  <ProtectedClaimsTool>
    <ClaimViewer {...props} />
  </ProtectedClaimsTool>
);

export const ProtectedClaimCategorization = (props) => (
  <ProtectedClaimsTool>
    <ClaimCategorization {...props} />
  </ProtectedClaimsTool>
);

export const ProtectedClaimEligibilityCheck = (props) => (
  <ProtectedClaimsTool>
    <ClaimEligibilityCheck {...props} />
  </ProtectedClaimsTool>
);

export const ProtectedClaimMakeDecision = (props) => (
  <ProtectedClaimsTool>
    <ClaimMakeDecision {...props} />
  </ProtectedClaimsTool>
);

export const ProtectedWallets = (props) => (
  <ProtectedRoute module="wallets" action="write">
    <Wallets {...props} />
  </ProtectedRoute>
);

export const ProtectedAddCompany = (props) => (
  <ProtectedRoute module="accounts" action="write">
    <AddCompany {...props} />
  </ProtectedRoute>
);

export const ProtectedViewCompany = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);

  return (
    <ProtectedRoute module="accounts" action="read">
      {isCrawOnly() ? (
        <ViewActiveBP {...props} /> // Show this for crawford users
      ) : (
        <ViewCompany {...props} /> // Default component
      )}
    </ProtectedRoute>
  );
};

export const ProtectedStats = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);

  return (
    <ProtectedRoute module="accounts" action="read">
      {isCrawOnly() ? <Forbidden /> : <StatsPage {...props} />}
    </ProtectedRoute>
  );
};

export const ProtectedServiceProvider = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);

  return (
    <ProtectedRoute module="accounts" action="read">
      {isCrawOnly() ? <ServiceProvider {...props} /> : <Forbidden />}
    </ProtectedRoute>
  );
};

export const ProtectedResources = (props) => {
  const { isCrawOnly } = React.useContext(AuthContext);

  return (
    <ProtectedRoute module="accounts" action="read">
      {isCrawOnly() ? <Resources {...props} /> : <Forbidden />}
    </ProtectedRoute>
  );
};

export const ProtectedMember = (props) => (
  <ProtectedRoute module="care" action="write">
    <Members {...props} />
  </ProtectedRoute>
);

export const ProtectedConsultations = (props) => (
  <ProtectedRoute module="care" action="read">
    <Consultations {...props} />
  </ProtectedRoute>
);

export const ProtectedWalletTransactions = (props) => (
  <ProtectedRoute module="wallets" action="write">
    <WalletTransactions {...props} />
  </ProtectedRoute>
);
